import React from 'react';
import type {
  ManageServiceProviderEventDialogEvent,
  UpdateServiceProviderEventValues,
} from './event-dialog-types';
import { EditEventDialog } from './edit-event-dialog';
import { DoneEventDialog } from './done-event-dialog';
import { CancelEventDialog } from './cancel-event-dialog';
import { DeleteEventDialog } from './delete-event-dialog';
import { CreateFollowupFromEventDialog } from './create-followup-from-event-dialog';
import { RegisterObservationFromEventDialog } from '../../follow-up-on-efforts/observations/register-observation-from-event-dialog';
import { WindowService } from '../../../../../components/window-service/window-service';
import { ServiceProviderRoute, ServiceProviderUrlBuilder } from '../../utils/service-provider-url-builder';

interface OccurrenceCallbacks {
  /** Called instead of onUpdate when acting on a single occurrence of a recurring series. */
  onUpdateOccurrence: (event: UpdateServiceProviderEventValues, occurrenceStartAt: Date) => Promise<void>;
  /** Called instead of onMarkDone when acting on a single occurrence. */
  onMarkOccurrenceDone: (seriesId: string, occurrenceStartAt: Date, comment?: string, consumedMinutes?: number) => Promise<void>;
  /** Called instead of onCancel when acting on a single occurrence. */
  onCancelOccurrence: (seriesId: string, occurrenceStartAt: Date, comment?: string, consumedMinutes?: number) => Promise<void>;
  /** Called instead of onDelete when acting on a single occurrence. */
  onDeleteOccurrence: (seriesId: string, occurrenceStartAt: Date) => Promise<void>;
}

interface EventDialogProps {
  event: ManageServiceProviderEventDialogEvent;
  onClose: () => void;
  onUpdate: (event: UpdateServiceProviderEventValues) => Promise<void>;
  onMarkDone: (
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) => Promise<void>;
  onCancel: (
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ) => Promise<void>;
  onUnmark: (eventId: string) => Promise<void>;
  onDelete: (eventId: string) => Promise<void>;
  /** Callbacks for per-occurrence actions — required when event.isSeriesOccurrence is true. */
  occurrenceCallbacks?: OccurrenceCallbacks;
  /** Called after a follow-up is successfully created so the host can reload. */
  onFollowupCreated?: () => void;
  /** Current user — pre-fills "Observeret af" when registering an observation on completion. */
  currentUserId?: string;
  /** Called after an observation is successfully registered so the host can reload. */
  onObservationCreated?: () => void;
}

type DialogMode = 'edit' | 'done' | 'cancel' | 'delete' | 'followup' | 'register-observation';

function isNotFutureDated(startAt: Date): boolean {
  return startAt.getTime() <= Date.now();
}

export function EventDialog(props: EventDialogProps) {
  const {
    event,
    onClose,
    onUpdate,
    onMarkDone,
    onCancel,
    onUnmark,
    onDelete,
    occurrenceCallbacks,
    onFollowupCreated,
    currentUserId,
    onObservationCreated,
  } = props;
  const [mode, setMode] = React.useState<DialogMode>('edit');
  // Set when mark-done succeeds on an observation-type-linked event, so the done dialog's
  // close transitions to the observation modal instead of dismissing. A ref (not state) so
  // the value is current when DoneEventDialog calls onClose synchronously after submit.
  const pendingObservationRef = React.useRef(false);

  // When acting on a single occurrence the dialog pre-fills with occurrence times.
  // The form edit uses occurrenceStartAt as the exclusion key alongside the edited values.
  const effectiveStartAt = event.isSeriesOccurrence && event.occurrenceStartAt
    ? event.occurrenceStartAt
    : event.startAt;
  const effectiveEndAt = event.isSeriesOccurrence && event.occurrenceEndAt
    ? event.occurrenceEndAt
    : event.endAt;

  function resolveUpdate(values: UpdateServiceProviderEventValues): Promise<void> {
    if (event.isSeriesOccurrence && occurrenceCallbacks && event.occurrenceStartAt) {
      return occurrenceCallbacks.onUpdateOccurrence(values, event.occurrenceStartAt);
    }
    return onUpdate(values);
  }

  function resolveMarkDone(
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ): Promise<void> {
    if (event.isSeriesOccurrence && occurrenceCallbacks && event.occurrenceStartAt) {
      return occurrenceCallbacks.onMarkOccurrenceDone(eventId, event.occurrenceStartAt, comment, consumedMinutes);
    }
    return onMarkDone(eventId, comment, consumedMinutes);
  }

  function resolveCancel(
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ): Promise<void> {
    if (event.isSeriesOccurrence && occurrenceCallbacks && event.occurrenceStartAt) {
      return occurrenceCallbacks.onCancelOccurrence(eventId, event.occurrenceStartAt, comment, consumedMinutes);
    }
    return onCancel(eventId, comment, consumedMinutes);
  }

  function resolveDelete(eventId: string): Promise<void> {
    if (event.isSeriesOccurrence && occurrenceCallbacks && event.occurrenceStartAt) {
      return occurrenceCallbacks.onDeleteOccurrence(eventId, event.occurrenceStartAt);
    }
    return onDelete(eventId);
  }

  // The event passed to sub-dialogs may need occurrence times for pre-filling.
  const effectiveEvent: ManageServiceProviderEventDialogEvent = {
    ...event,
    startAt: effectiveStartAt,
    endAt: effectiveEndAt,
    // Occurrence editing does not carry forward recurrence — the child is a one-off.
    recurrence: event.isSeriesOccurrence ? undefined : event.recurrence,
  };

  const hasPartialObjectiveContext =
    !!event.citizenId && !!event.partialObjectiveId;
  const canCreateFollowup = isNotFutureDated(event.startAt);

  const hasObservationTypeContext =
    !!event.citizenId && !!event.observationTypeId;

  function resolveMarkDoneThenObservation(
    eventId: string,
    comment?: string,
    consumedMinutes?: number
  ): Promise<void> {
    return resolveMarkDone(eventId, comment, consumedMinutes).then(() => {
      if (hasObservationTypeContext) {
        pendingObservationRef.current = true;
      }
    });
  }

  function handleDoneClose() {
    if (pendingObservationRef.current) {
      pendingObservationRef.current = false;
      setMode('register-observation');
    } else {
      onClose();
    }
  }

  function goToPartialObjective() {
    if (!event.citizenId) {
      return;
    }

    const url = ServiceProviderUrlBuilder.build(ServiceProviderRoute.PARTIAL_OBJECTIVES_OBSERVATIONS, {
      citizenId: event.citizenId,
      spuId: event.serviceProviderUnitId,
    });
    WindowService.open(url, url);
  }

  if (mode === 'followup' && event.citizenId && event.partialObjectiveId) {
    return (
      <CreateFollowupFromEventDialog
        citizenId={event.citizenId}
        eventId={event.eventId}
        partialObjectiveId={event.partialObjectiveId}
        eventStartDate={event.startAt}
        onClose={onClose}
        onCreated={() => {
          onFollowupCreated?.();
        }}
      />
    );
  }

  if (mode === 'edit') {
    return (
      <EditEventDialog
        event={effectiveEvent}
        onClose={onClose}
        onCancel={() => setMode('cancel')}
        onDone={() => setMode('done')}
        onUnmark={onUnmark}
        onDelete={() => setMode('delete')}
        onSubmit={resolveUpdate}
        partialObjectiveActions={
          hasPartialObjectiveContext
            ? {
                onCreateFollowup: () => setMode('followup'),
                canCreateFollowup,
                onGoToPartialObjective: goToPartialObjective,
              }
            : undefined
        }
      />
    );
  }

  if (mode === 'done') {
    return (
      <DoneEventDialog
        event={effectiveEvent}
        onClose={handleDoneClose}
        onBack={() => setMode('edit')}
        onSubmit={resolveMarkDoneThenObservation}
      />
    );
  }

  if (mode === 'register-observation' && event.citizenId && event.observationTypeId) {
    return (
      <RegisterObservationFromEventDialog
        citizenId={event.citizenId}
        serviceProviderUnitId={event.serviceProviderUnitId}
        observationTypeId={event.observationTypeId}
        eventStartDate={event.startAt}
        currentUserId={currentUserId}
        onClose={onClose}
        onCreated={() => {
          onObservationCreated?.();
        }}
      />
    );
  }

  if (mode === 'cancel') {
    return (
      <CancelEventDialog
        event={effectiveEvent}
        onClose={onClose}
        onBack={() => setMode('edit')}
        onSubmit={resolveCancel}
      />
    );
  }

  return (
    <DeleteEventDialog
      event={effectiveEvent}
      onClose={onClose}
      onBack={() => setMode('edit')}
      onSubmit={resolveDelete}
    />
  );
}
